// ===========================================
// Teacher Retention Chart Component
// ===========================================
// Bar chart showing teacher retention rates

import { Card, CardContent, CardHeader, Typography, Box, Divider, CircularProgress } from '@mui/material';
import { People as PeopleIcon } from '@mui/icons-material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
} from 'recharts';

import { TeacherRetentionData } from '../../api/reports.api';

// ===========================================
// TYPES
// ===========================================

interface TeacherRetentionChartProps {
  data: TeacherRetentionData[];
  loading?: boolean;
}

// ===========================================
// CUSTOM TOOLTIP
// ===========================================

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;

  return (
    <Card sx={{ p: 1.5, minWidth: 220 }}>
      <Typography variant="body2" fontWeight={600} gutterBottom>
        {label}
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption">Retention Rate:</Typography>
          <Typography variant="caption" fontWeight={600}>
            {data.retentionRate.toFixed(1)}%
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption">Active Students:</Typography>
          <Typography variant="caption" fontWeight={600}>
            {data.activeStudents}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption">Total Enrolments:</Typography>
          <Typography variant="caption" fontWeight={600}>
            {data.totalEnrollments}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption">Avg Tenure:</Typography>
          <Typography variant="caption" fontWeight={600}>
            {data.averageStudentTenure.toFixed(0)} days
          </Typography>
        </Box>
      </Box>
    </Card>
  );
}

// ===========================================
// COMPONENT
// ===========================================

export default function TeacherRetentionChart({ data, loading }: TeacherRetentionChartProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon color="primary" />
              <Typography variant="h6">Student Retention by Teacher</Typography>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon color="primary" />
              <Typography variant="h6">Student Retention by Teacher</Typography>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No retention data available
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Sort by retention rate descending, take top 10
  const sortedData = [...data]
    .sort((a, b) => b.retentionRate - a.retentionRate)
    .slice(0, 10)
    .map((item) => ({
      ...item,
      name: item.teacherName.split(' ')[0], // First name only
    }));

  // Color based on retention rate
  const getColor = (rate: number) => {
    if (rate >= 80) return '#4caf50'; // Green
    if (rate >= 60) return '#FFCE00'; // Yellow
    return '#f44336'; // Red
  };

  return (
    <Card>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon color="primary" />
            <Typography variant="h6">Student Retention by Teacher</Typography>
          </Box>
        }
        subheader="Retention rate: active students / total enrolments"
      />
      <Divider />
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={sortedData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={false}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={false}
              label={{ value: 'Retention Rate (%)', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="retentionRate" name="Retention Rate (%)" radius={[4, 4, 0, 0]}>
              {sortedData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getColor(entry.retentionRate)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
